// /tools: every widget the company has, with create and delete.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Code2, Paintbrush, Plus, SquareStack, Trash2 } from "lucide-react";
import { ProductIcon, productOf } from "../../components/ProductIcon";
import { useApi, type Widget } from "../../api";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { StatusChip } from "../../components/StatusChip";
import "../../widgets.css";

// How each widget style is named in the list
const STYLE_NAMES: Record<string, string> = { small: "Compact", full: "Full width", hosted: "Hosted page" };

export function WidgetsPage() {
  const api = useApi();
  const [widgets, setWidgets] = useState<Widget[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The widget waiting for delete confirmation (the dialog is open while set)
  const [deleting, setDeleting] = useState<Widget | null>(null);
  const [busy, setBusy] = useState(false);

  // Load the list once
  useEffect(() => {
    api
      .listWidgets()
      .then(setWidgets)
      .catch((e: Error) => setError(e.message));
  }, [api]);

  async function remove(w: Widget) {
    setBusy(true);
    try {
      await api.deleteWidget(w.widgetId);
      setWidgets((list) => list?.filter((x) => x.widgetId !== w.widgetId) ?? null);
      setDeleting(null);
    } catch (err) {
      setError((err as Error).message);
      setDeleting(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1>Website components</h1>
        <p>The calculators and pages you put on your website. Each has its own branding, pricing and setup, so you can run different ones on different websites, pages and flyers.</p>
      </div>

      {/* Create: a short wizard (name + Compact / Full width) */}
      <div className="new-widget-cta">
        <Link to="/components/new" className="btn btn-primary">
          <Plus size={16} aria-hidden="true" /> New component
        </Link>
      </div>

      {error && <div className="flash flash-danger" role="alert" style={{ marginTop: 16 }}>{error}</div>}

      {/* List */}
      {widgets === null && !error && <p className="muted" style={{ marginTop: 24 }}>Loading components…</p>}
      {widgets?.length === 0 && (
        <div className="card empty-widgets">
          <SquareStack size={28} aria-hidden="true" />
          <p>No website components yet. Create your first one above.</p>
        </div>
      )}
      {widgets && widgets.length > 0 && (
        <div className="widget-grid">
          {widgets.map((w) => (
            <div key={w.widgetId} className="card widget-card">
              <div className="widget-card-top">
                <Link to={`/components/${w.widgetId}/branding`} className="widget-card-name">
                  {w.name}
                </Link>
                <StatusChip widget={w} />
              </div>
              {/* What it is: the widget type and its style */}
              <div className="widget-card-type">
                {/* The product's coloured icon: heat pump, solar (docs/12) or contact form */}
                <ProductIcon product={productOf(w)} size={22} />
                {w.product === "solar"
                  ? `Solar estimate · ${STYLE_NAMES[w.branding?.layout.style ?? "full"] ?? "Full width"}`
                  : w.branding?.layout.style === "contact"
                    ? "Contact form"
                    : `ASHP Estimator · ${STYLE_NAMES[w.branding?.layout.style ?? "small"]}`}
              </div>
              <div className="widget-card-meta">
                <span>Updated {new Date(w.updatedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
              </div>
              <div className="widget-card-actions">
                <Link to={`/components/${w.widgetId}/branding`} className="btn btn-primary">
                  <Paintbrush size={16} aria-hidden="true" /> Customise
                </Link>
                <Link to={`/components/${w.widgetId}/install`} className="btn btn-secondary">
                  <Code2 size={16} aria-hidden="true" /> Install
                </Link>
                <button type="button" className="btn btn-danger-outline widget-card-trash" onClick={() => setDeleting(w)} title="Delete component" aria-label={`Delete ${w.name}`}>
                  <Trash2 size={16} aria-hidden="true" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete: a proper warning before anything is removed */}
      <ConfirmDialog
        open={deleting !== null}
        title={deleting ? `Delete “${deleting.name}”?` : "Delete component?"}
        confirmLabel="Delete component"
        busy={busy}
        onConfirm={() => deleting && void remove(deleting)}
        onCancel={() => setDeleting(null)}
      >
        <p>This can't be undone. Any website using this calculator's snippet will stop showing it{deleting?.hostedUrl ? ", and its hosted page and QR code will stop working" : ""}.</p>
        <p>Its settings, prices and analytics are deleted with it.</p>
      </ConfirmDialog>
    </div>
  );
}
